/**
 * Wizard-State-Store.
 *
 * Persistiert den Bearbeitungsstand pro Case in localStorage. Plan-Ziel ist
 * IndexedDB für Offline-Resilienz – das wird im Konsolidierungs-Schritt mit
 * `idb-keyval` ergänzt. Die Storage-Adapter-Schicht ist absichtlich klein,
 * damit der Tausch trivial bleibt.
 *
 * Sicherheit: Auch im Stub speichern wir nur Felder, die nicht hochsensibel
 * sind (Stub-Texte). Sobald BWA-Rohdaten dazukommen (AP6b), muss die
 * Persistenz verschlüsselt werden – siehe TODO-Marker in computeStorage().
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { DimensionKey, StepKey } from '../../components/hub/dimensions';
import type { BgRole, ResourcesData, SpecialNeed } from '@tragfaehigkeit/core';

export type StepStatus = 'untouched' | 'in-progress' | 'complete';

export interface DimensionDataDraft {
  /** Wann zuletzt im UI bearbeitet (ISO-String) */
  readonly lastEditedAt: string | null;
  /** Bearbeitungsstand im Wizard */
  readonly status: StepStatus;
  /**
   * Frei-Form-Felder pro Dimension. AP6 hat pro Dimension nur ein einziges
   * echtes Eingabefeld (Stub) – die Schemas in @tragfaehigkeit/core sind die
   * spätere Wahrheit, hier reicht ein offenes Record.
   */
  readonly fields: Readonly<Record<string, string>>;
  /**
   * Zwei-Ebenen-Modell: Zeitstempel der fachlichen Prüfung durch den Berater.
   * Nur auf der Berater-Ebene (`beraterDimensionData`) fachlich relevant; auf
   * der Mandanten-Ebene (`dimensionData`) bleibt es null.
   */
  readonly reviewedAt: string | null;
}

export interface TriageDraft {
  readonly status: StepStatus;
  readonly lastEditedAt: string | null;
  readonly legalBasis: string | null;
}

/**
 * Resources-Slice (Dimension 4 — AP6b).
 *
 * Hält die strukturierten Finanz- und BG-Daten als Cents/Number, damit die
 * Score-Engine direkt aus diesem Slice gefüttert werden kann.
 *
 * TODO: AP2.5 — diese Daten sind sensibel (Sozialdaten nach § 35 SGB I,
 * Art. 9 DSGVO). Sobald BWA-Werte echt sind, vor Persistierung verschlüsseln
 * (libsodium / Web Crypto API). Aktuell rein lokaler Persistenz-Stub im
 * Browser-Profil des Mandanten.
 */
export interface ResourcesBgMemberDraft {
  readonly id: string;
  readonly role: BgRole;
  readonly age: number;
  readonly specialNeeds: readonly SpecialNeed[];
}

export interface ResourcesKduDraft {
  readonly kaltCents: number;
  readonly nebenCents: number;
  readonly heizCents: number;
}

export interface ResourcesDraft {
  readonly status: StepStatus;
  readonly lastEditedAt: string | null;
  /** Aktuell aktiver Sub-Step (4.1 – 4.5). */
  readonly activeSubStep: ResourcesSubStepKey;
  // BWA (4.1)
  readonly revenueCents: number | null;
  readonly costsCents: number | null;
  // Stundensatz (4.2)
  readonly hoursPerMonth: number | null;
  // Liquidität (4.3)
  readonly liquidityCents: number | null;
  /** Burn-Rate; default = costsCents wenn unverändert (`null` = Default verwenden). */
  readonly burnRateCents: number | null;
  /** Kurzfristige Verbindlichkeiten (senkt Netto-Liquidität/Runway). */
  readonly shortTermLiabilitiesCents: number | null;
  // BG (4.4)
  readonly bgComposition: readonly ResourcesBgMemberDraft[];
  readonly kdu: ResourcesKduDraft;
  // SGB-II-Anrechnung (4.5)
  /** Brutto-Erwerbseinkommen (§ 11 SGB II) — Bemessungsgrundlage der Lücke. */
  readonly bruttoErwerbseinkommenCents: number | null;
  /** Sonstiges anrechenbares BG-Einkommen (Kindergeld, Elterngeld, Unterhalt …). */
  readonly sonstigesEinkommenCents: number | null;
}

export type ResourcesSubStepKey = 'bwa' | 'rate' | 'liquidity' | 'bg' | 'luecke';
export const RESOURCES_SUB_STEPS: readonly ResourcesSubStepKey[] = [
  'bwa',
  'rate',
  'liquidity',
  'bg',
  'luecke',
] as const;

/**
 * Bewertungs-Texte pro Case (AP10).
 *
 * Spiegel der `AssessmentTexts` aus `@tragfaehigkeit/core/statement`, aber
 * im Store immer als `Record<string, string>` gespeichert — leere Strings
 * statt undefined, damit Form-Komponenten eine stabile Identität haben.
 *
 * Die Schlüssel sind 1:1 die Platzhalter-Namen aus
 * `docs/Platzhalter_Referenz.md` (in camelCase). Der Adapter
 * `selectAssessmentsForCase` (in `lib/statement/assessmentsAdapter.ts`)
 * verwandelt den Store-Slice in das von Core erwartete Format.
 */
export interface AssessmentDraft {
  readonly lastEditedAt: string | null;
  /** Top-Level-Texte */
  readonly revenueAssessment: string;
  readonly costsAssessment: string;
  readonly profitAssessment: string;
  readonly hourlyRateAssessment: string;
  readonly liquidityAssessment: string;
  readonly runwayAssessment: string;
  readonly utilizationAssessment: string;
  readonly gapInterpretation: string;
  readonly verdictLongParagraph: string;
  readonly verdictReasoning: string;
  readonly recommendationDetailed: string;
  readonly conditionsParagraph: string;
  readonly reevaluationProposal: string;
  readonly constraintDiscussion: string;
  readonly riskIndicators: string;
  readonly strengthIndicators: string;
  /** Pro Dimension: short + detail */
  readonly dimensionTexts: Readonly<
    Record<DimensionKey, { readonly short: string; readonly detail: string }>
  >;
}

export interface WizardCaseState {
  readonly caseId: string;
  readonly currentStep: StepKey;
  readonly triage: TriageDraft;
  /**
   * Mandanten-Ebene (Erfassung durch den Kunden). Entspricht `layer='mandant'`
   * in `dimension_data` (siehe supabase/migrations/0028_two_layer_capture.sql).
   */
  readonly dimensionData: Readonly<Record<DimensionKey, DimensionDataDraft>>;
  /**
   * Berater-Ebene (fachliche Bearbeitung). Entspricht `layer='berater'`. Für
   * den Kunden unsichtbar; speist Score und Stellungnahme (wirksamer Wert =
   * Berater-Wert, sonst Mandanten-Angabe, siehe @tragfaehigkeit/core `merge`).
   */
  readonly beraterDimensionData: Readonly<Record<DimensionKey, DimensionDataDraft>>;
  readonly resources: ResourcesDraft;
  readonly assessments: AssessmentDraft;
  readonly lastSavedAt: string | null;
}

/** Schlüssel der Top-Level-Bewertungs-Texte (für die Editor-UI). */
export type AssessmentScalarKey = Exclude<keyof AssessmentDraft, 'lastEditedAt' | 'dimensionTexts'>;

interface WizardStore {
  readonly cases: Readonly<Record<string, WizardCaseState>>;
  hydrated: boolean;
  ensureCase: (caseId: string) => void;
  setCurrentStep: (caseId: string, step: StepKey) => void;
  setTriage: (caseId: string, patch: Partial<Omit<TriageDraft, 'lastEditedAt'>>) => void;
  setDimensionField: (
    caseId: string,
    dimension: DimensionKey,
    fieldName: string,
    value: string,
  ) => void;
  markDimensionComplete: (caseId: string, dimension: DimensionKey) => void;
  // Berater-Ebene (Zwei-Ebenen-Modell): fachliche Bearbeitung + Prüfung.
  setBeraterDimensionField: (
    caseId: string,
    dimension: DimensionKey,
    fieldName: string,
    value: string,
  ) => void;
  markBeraterDimensionReviewed: (caseId: string, dimension: DimensionKey) => void;
  /**
   * Setzt die (read-only) Mandanten-Ebene aus einem geladenen Stand (Sync im
   * Berater-Mount). Ersetzt nur `dimensionData`, lässt die Berater-Ebene intakt.
   */
  setMandantLayerSnapshot: (
    caseId: string,
    snapshot: Readonly<Record<DimensionKey, DimensionDataDraft>>,
  ) => void;
  // Resources-spezifische Aktionen
  setResourcesSubStep: (caseId: string, sub: ResourcesSubStepKey) => void;
  setResourcesField: <K extends keyof ResourcesPrimitivePatch>(
    caseId: string,
    field: K,
    value: ResourcesPrimitivePatch[K],
  ) => void;
  setResourcesKdu: (caseId: string, patch: Partial<ResourcesKduDraft>) => void;
  addBgMember: (caseId: string, member: Omit<ResourcesBgMemberDraft, 'id'>) => void;
  updateBgMember: (
    caseId: string,
    memberId: string,
    patch: Partial<Omit<ResourcesBgMemberDraft, 'id'>>,
  ) => void;
  removeBgMember: (caseId: string, memberId: string) => void;
  // Bewertungs-Editor (AP10)
  setAssessmentScalar: (caseId: string, field: AssessmentScalarKey, value: string) => void;
  setAssessmentDimension: (
    caseId: string,
    dimension: DimensionKey,
    patch: { readonly short?: string; readonly detail?: string },
  ) => void;
  replaceAssessments: (caseId: string, next: Omit<AssessmentDraft, 'lastEditedAt'>) => void;
  resetAssessments: (caseId: string) => void;
  resetCase: (caseId: string) => void;
  /**
   * Ersetzt den Case-State komplett mit einem aus der DB geladenen Stand.
   * Wird vom useWizardSync-Hook verwendet — KEIN Update der lastSavedAt-
   * Marker beim Setzen, weil der Stand aus der DB kommt.
   */
  replaceCaseFromDb: (caseId: string, next: WizardCaseState) => void;
}

type ResourcesPrimitivePatch = Pick<
  ResourcesDraft,
  | 'revenueCents'
  | 'costsCents'
  | 'hoursPerMonth'
  | 'liquidityCents'
  | 'burnRateCents'
  | 'shortTermLiabilitiesCents'
  | 'bruttoErwerbseinkommenCents'
  | 'sonstigesEinkommenCents'
>;

export function makeEmptyDimensions(): Record<DimensionKey, DimensionDataDraft> {
  const empty = (): DimensionDataDraft => ({
    lastEditedAt: null,
    status: 'untouched',
    fields: {},
    reviewedAt: null,
  });
  return {
    identity: empty(),
    activity: empty(),
    style: empty(),
    resources: empty(),
    health: empty(),
    private: empty(),
  };
}

const EMPTY_KDU: ResourcesKduDraft = Object.freeze({
  kaltCents: 0,
  nebenCents: 0,
  heizCents: 0,
});

function makeEmptyAssessments(): AssessmentDraft {
  return {
    lastEditedAt: null,
    revenueAssessment: '',
    costsAssessment: '',
    profitAssessment: '',
    hourlyRateAssessment: '',
    liquidityAssessment: '',
    runwayAssessment: '',
    utilizationAssessment: '',
    gapInterpretation: '',
    verdictLongParagraph: '',
    verdictReasoning: '',
    recommendationDetailed: '',
    conditionsParagraph: '',
    reevaluationProposal: '',
    constraintDiscussion: '',
    riskIndicators: '',
    strengthIndicators: '',
    dimensionTexts: {
      identity: { short: '', detail: '' },
      activity: { short: '', detail: '' },
      style: { short: '', detail: '' },
      resources: { short: '', detail: '' },
      health: { short: '', detail: '' },
      private: { short: '', detail: '' },
    },
  };
}

function makeInitialResources(): ResourcesDraft {
  // Vorbelegung: Antragsteller mit Default-Alter — kann sofort editiert werden.
  return {
    status: 'untouched',
    lastEditedAt: null,
    activeSubStep: 'bwa',
    revenueCents: null,
    costsCents: null,
    hoursPerMonth: null,
    liquidityCents: null,
    burnRateCents: null,
    shortTermLiabilitiesCents: null,
    bgComposition: [
      {
        id: makeId(),
        role: 'antragsteller',
        age: 35,
        specialNeeds: [],
      },
    ],
    kdu: { ...EMPTY_KDU },
    bruttoErwerbseinkommenCents: null,
    sonstigesEinkommenCents: null,
  };
}

function makeInitialCase(caseId: string): WizardCaseState {
  return {
    caseId,
    currentStep: 'triage',
    triage: { status: 'untouched', lastEditedAt: null, legalBasis: null },
    dimensionData: makeEmptyDimensions(),
    beraterDimensionData: makeEmptyDimensions(),
    resources: makeInitialResources(),
    assessments: makeEmptyAssessments(),
    lastSavedAt: null,
  };
}

function makeId(): string {
  // crypto.randomUUID gibt es in modernen Browsern und Node ≥ 19. Fallback
  // für ältere SSR-Pfade, die wir hier theoretisch berühren könnten.
  if (typeof globalThis.crypto !== 'undefined' && 'randomUUID' in globalThis.crypto) {
    return globalThis.crypto.randomUUID();
  }
  return `bg_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

/**
 * Storage-Wrapper. Aktuell localStorage; soll später durch IndexedDB ersetzt
 * werden. Application-Layer-Crypto ist Pflicht, sobald die Felder echte
 * Sozialdaten enthalten (Phase 2 / AP6b).
 */
function getStorage() {
  // TODO(AP6b): Wechsel auf IndexedDB + libsodium-Verschlüsselung
  return createJSONStorage(() => {
    if (typeof window === 'undefined') {
      // SSR/Test ohne window – pseudo-Storage, damit Zustand nicht crasht
      const mem = new Map<string, string>();
      return {
        getItem: (k: string) => mem.get(k) ?? null,
        setItem: (k: string, v: string) => {
          mem.set(k, v);
        },
        removeItem: (k: string) => {
          mem.delete(k);
        },
      };
    }
    return window.localStorage;
  });
}

/**
 * Migration: Alte persistierte Cases (vor AP6b) hatten kein `resources`-Feld.
 * Wir hängen ein Default-Resources an, damit das UI nicht crasht.
 */
/**
 * Backfill: alte Dimension-Drafts (vor 0028/v4) hatten kein `reviewedAt`.
 * Ergänzt es defensiv, ohne bestehende Werte zu überschreiben.
 */
function backfillDimensionReviewedAt(
  dims: Readonly<Record<DimensionKey, DimensionDataDraft>> | undefined,
): Readonly<Record<DimensionKey, DimensionDataDraft>> {
  if (!dims) return makeEmptyDimensions();
  const out = makeEmptyDimensions();
  for (const key of Object.keys(out) as DimensionKey[]) {
    const existing = dims[key];
    if (existing) {
      out[key] = { ...existing, reviewedAt: existing.reviewedAt ?? null };
    }
  }
  return out;
}

function migrateCase(c: WizardCaseState | undefined, caseId: string): WizardCaseState {
  if (!c) return makeInitialCase(caseId);
  let next: WizardCaseState = c;
  if (!next.resources) {
    next = { ...next, resources: makeInitialResources() };
  }
  if (!next.assessments) {
    next = { ...next, assessments: makeEmptyAssessments() };
  }
  // v3 → v4 (0028): reviewedAt pro Dimension + Berater-Ebene.
  next = { ...next, dimensionData: backfillDimensionReviewedAt(next.dimensionData) };
  if (!next.beraterDimensionData) {
    next = { ...next, beraterDimensionData: makeEmptyDimensions() };
  } else {
    next = {
      ...next,
      beraterDimensionData: backfillDimensionReviewedAt(next.beraterDimensionData),
    };
  }
  return next;
}

function recomputeResourcesStatus(r: ResourcesDraft): StepStatus {
  // complete: Pflichtfelder ausgefüllt UND Status wurde explizit auf complete
  // gesetzt (über setCurrentStep nach Sub-Step 4.5). Hier deriven wir den
  // automatischen "in-progress"-Status. complete bleibt sticky.
  if (r.status === 'complete') return 'complete';
  const anyValue =
    r.revenueCents !== null ||
    r.costsCents !== null ||
    r.hoursPerMonth !== null ||
    r.liquidityCents !== null ||
    r.burnRateCents !== null ||
    r.kdu.kaltCents > 0 ||
    r.kdu.nebenCents > 0 ||
    r.kdu.heizCents > 0;
  return anyValue ? 'in-progress' : 'untouched';
}

export const useWizardStore = create<WizardStore>()(
  persist(
    (set, get) => ({
      cases: {},
      hydrated: false,

      ensureCase: (caseId) => {
        const existing = get().cases[caseId];
        if (existing) {
          // Migrate falls aus einer pre-AP6b-Persistenz geladen
          const migrated = migrateCase(existing, caseId);
          if (migrated !== existing) {
            set((s) => ({ cases: { ...s.cases, [caseId]: migrated } }));
          }
          return;
        }
        set((s) => ({
          cases: { ...s.cases, [caseId]: makeInitialCase(caseId) },
        }));
      },

      setCurrentStep: (caseId, step) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          return {
            cases: { ...s.cases, [caseId]: { ...c, currentStep: step } },
          };
        });
      },

      setTriage: (caseId, patch) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          const nextTriage: TriageDraft = {
            ...c.triage,
            ...patch,
            lastEditedAt: nowIso(),
            status: patch.legalBasis !== undefined && patch.legalBasis ? 'complete' : 'in-progress',
          };
          return {
            cases: {
              ...s.cases,
              [caseId]: { ...c, triage: nextTriage, lastSavedAt: nowIso() },
            },
          };
        });
      },

      setDimensionField: (caseId, dimension, fieldName, value) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          const current = c.dimensionData[dimension];
          const nextFields = { ...current.fields, [fieldName]: value };
          // Status: alles leer → untouched, sonst in-progress (complete erst bei
          // expliziter Bestätigung via markDimensionComplete).
          const anyValue = Object.values(nextFields).some((v) => v.trim().length > 0);
          const nextStatus: StepStatus =
            current.status === 'complete' ? 'complete' : anyValue ? 'in-progress' : 'untouched';
          return {
            cases: {
              ...s.cases,
              [caseId]: {
                ...c,
                lastSavedAt: nowIso(),
                dimensionData: {
                  ...c.dimensionData,
                  [dimension]: {
                    fields: nextFields,
                    lastEditedAt: nowIso(),
                    status: nextStatus,
                    reviewedAt: current.reviewedAt,
                  },
                },
              },
            },
          };
        });
      },

      markDimensionComplete: (caseId, dimension) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          const current = c.dimensionData[dimension];
          // Resources hat seinen eigenen strukturierten Slice. Den status spiegeln
          // wir auf complete, damit Hub-Display einheitlich funktioniert.
          const nextResources =
            dimension === 'resources'
              ? { ...c.resources, status: 'complete' as StepStatus, lastEditedAt: nowIso() }
              : c.resources;
          return {
            cases: {
              ...s.cases,
              [caseId]: {
                ...c,
                lastSavedAt: nowIso(),
                resources: nextResources,
                dimensionData: {
                  ...c.dimensionData,
                  [dimension]: { ...current, status: 'complete', lastEditedAt: nowIso() },
                },
              },
            },
          };
        });
      },

      // ── Berater-Ebene (Zwei-Ebenen-Modell) ────────────────────────────────
      setBeraterDimensionField: (caseId, dimension, fieldName, value) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          const current = c.beraterDimensionData[dimension];
          const nextFields = { ...current.fields, [fieldName]: value };
          const anyValue = Object.values(nextFields).some((v) => v.trim().length > 0);
          const nextStatus: StepStatus =
            current.status === 'complete' ? 'complete' : anyValue ? 'in-progress' : 'untouched';
          return {
            cases: {
              ...s.cases,
              [caseId]: {
                ...c,
                lastSavedAt: nowIso(),
                beraterDimensionData: {
                  ...c.beraterDimensionData,
                  [dimension]: {
                    fields: nextFields,
                    lastEditedAt: nowIso(),
                    status: nextStatus,
                    // Neue fachliche Eingabe hebt die frühere Prüfung auf.
                    reviewedAt: null,
                  },
                },
              },
            },
          };
        });
      },

      markBeraterDimensionReviewed: (caseId, dimension) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          const current = c.beraterDimensionData[dimension];
          return {
            cases: {
              ...s.cases,
              [caseId]: {
                ...c,
                beraterDimensionData: {
                  ...c.beraterDimensionData,
                  [dimension]: { ...current, reviewedAt: nowIso() },
                },
              },
            },
          };
        });
      },

      setMandantLayerSnapshot: (caseId, snapshot) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          return {
            cases: {
              ...s.cases,
              [caseId]: { ...c, dimensionData: backfillDimensionReviewedAt(snapshot) },
            },
          };
        });
      },

      setResourcesSubStep: (caseId, sub) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          return {
            cases: {
              ...s.cases,
              [caseId]: { ...c, resources: { ...c.resources, activeSubStep: sub } },
            },
          };
        });
      },

      setResourcesField: (caseId, field, value) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          const nextResources: ResourcesDraft = {
            ...c.resources,
            [field]: value,
            lastEditedAt: nowIso(),
          };
          const nextStatus = recomputeResourcesStatus(nextResources);
          return {
            cases: {
              ...s.cases,
              [caseId]: {
                ...c,
                lastSavedAt: nowIso(),
                resources: { ...nextResources, status: nextStatus },
                dimensionData: {
                  ...c.dimensionData,
                  resources: {
                    ...c.dimensionData.resources,
                    status: nextStatus,
                    lastEditedAt: nowIso(),
                  },
                },
              },
            },
          };
        });
      },

      setResourcesKdu: (caseId, patch) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          const nextKdu: ResourcesKduDraft = { ...c.resources.kdu, ...patch };
          const nextResources: ResourcesDraft = {
            ...c.resources,
            kdu: nextKdu,
            lastEditedAt: nowIso(),
          };
          const nextStatus = recomputeResourcesStatus(nextResources);
          return {
            cases: {
              ...s.cases,
              [caseId]: {
                ...c,
                lastSavedAt: nowIso(),
                resources: { ...nextResources, status: nextStatus },
                dimensionData: {
                  ...c.dimensionData,
                  resources: {
                    ...c.dimensionData.resources,
                    status: nextStatus,
                    lastEditedAt: nowIso(),
                  },
                },
              },
            },
          };
        });
      },

      addBgMember: (caseId, member) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          const nextMembers = [...c.resources.bgComposition, { id: makeId(), ...member }];
          return {
            cases: {
              ...s.cases,
              [caseId]: {
                ...c,
                lastSavedAt: nowIso(),
                resources: {
                  ...c.resources,
                  bgComposition: nextMembers,
                  lastEditedAt: nowIso(),
                },
              },
            },
          };
        });
      },

      updateBgMember: (caseId, memberId, patch) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          const nextMembers = c.resources.bgComposition.map((m) =>
            m.id === memberId ? { ...m, ...patch } : m,
          );
          return {
            cases: {
              ...s.cases,
              [caseId]: {
                ...c,
                lastSavedAt: nowIso(),
                resources: {
                  ...c.resources,
                  bgComposition: nextMembers,
                  lastEditedAt: nowIso(),
                },
              },
            },
          };
        });
      },

      removeBgMember: (caseId, memberId) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          // Antragsteller darf nicht gelöscht werden — Schema erfordert genau 1.
          const target = c.resources.bgComposition.find((m) => m.id === memberId);
          if (!target || target.role === 'antragsteller') return s;
          const nextMembers = c.resources.bgComposition.filter((m) => m.id !== memberId);
          return {
            cases: {
              ...s.cases,
              [caseId]: {
                ...c,
                lastSavedAt: nowIso(),
                resources: {
                  ...c.resources,
                  bgComposition: nextMembers,
                  lastEditedAt: nowIso(),
                },
              },
            },
          };
        });
      },

      // ── Bewertungs-Editor (AP10) ──────────────────────────────────────────
      setAssessmentScalar: (caseId, field, value) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          return {
            cases: {
              ...s.cases,
              [caseId]: {
                ...c,
                lastSavedAt: nowIso(),
                assessments: {
                  ...c.assessments,
                  [field]: value,
                  lastEditedAt: nowIso(),
                },
              },
            },
          };
        });
      },

      setAssessmentDimension: (caseId, dimension, patch) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          const current = c.assessments.dimensionTexts[dimension];
          const nextDim = {
            short: patch.short ?? current.short,
            detail: patch.detail ?? current.detail,
          };
          return {
            cases: {
              ...s.cases,
              [caseId]: {
                ...c,
                lastSavedAt: nowIso(),
                assessments: {
                  ...c.assessments,
                  dimensionTexts: {
                    ...c.assessments.dimensionTexts,
                    [dimension]: nextDim,
                  },
                  lastEditedAt: nowIso(),
                },
              },
            },
          };
        });
      },

      replaceAssessments: (caseId, next) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          return {
            cases: {
              ...s.cases,
              [caseId]: {
                ...c,
                lastSavedAt: nowIso(),
                assessments: { ...next, lastEditedAt: nowIso() },
              },
            },
          };
        });
      },

      resetAssessments: (caseId) => {
        set((s) => {
          const c = migrateCase(s.cases[caseId], caseId);
          return {
            cases: {
              ...s.cases,
              [caseId]: { ...c, assessments: makeEmptyAssessments() },
            },
          };
        });
      },

      resetCase: (caseId) => {
        set((s) => ({
          cases: { ...s.cases, [caseId]: makeInitialCase(caseId) },
        }));
      },

      replaceCaseFromDb: (caseId, next) => {
        // Defensive: caseId in next muss matchen, sonst korrumpieren wir den
        // Map-Key. Aufrufer passiert die normalisierte Struktur durch. Der
        // DB-Load liefert beide Ebenen (RLS-gefiltert) — voller Ersatz.
        const normalized: WizardCaseState = next.caseId === caseId ? next : { ...next, caseId };
        set((s) => ({ cases: { ...s.cases, [caseId]: normalized } }));
      },
    }),
    {
      name: 'tragfaehigkeit:wizard',
      version: 4,
      storage: getStorage(),
      partialize: (state) => ({ cases: state.cases }),
      // Migrationen:
      //  v1 → v2: AP6b — `resources`-Slice eingeführt.
      //  v2 → v3: AP10 — `assessments`-Slice eingeführt.
      //  v3 → v4: 0028 — `reviewedAt` pro Dimension + Berater-Ebene.
      // Alle Schritte werden idempotent von migrateCase() abgedeckt.
      migrate: (persisted: unknown, _version) => {
        void _version;
        const ps = persisted as { cases?: Record<string, WizardCaseState> } | null;
        if (!ps || typeof ps !== 'object') return { cases: {} };
        const next: Record<string, WizardCaseState> = {};
        for (const [caseId, c] of Object.entries(ps.cases ?? {})) {
          next[caseId] = migrateCase(c, caseId);
        }
        return { cases: next };
      },
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.hydrated = true;
        }
      },
    },
  ),
);

/** Convenience-Selector: Status pro Step für den Hub-Progress. */
export function selectStepStatus(c: WizardCaseState | undefined, step: StepKey): StepStatus {
  if (!c) return 'untouched';
  if (step === 'triage') return c.triage.status;
  if (step === 'resources') {
    // Resources hat zusätzlich strukturierte Daten — der Slice-Status hat
    // Vorrang vor dem Stub-Status im dimensionData.
    return c.resources.status;
  }
  return c.dimensionData[step].status;
}

/** Anzahl abgeschlossener Steps (Triage + 6 Dimensionen). */
export function selectCompletedCount(c: WizardCaseState | undefined): number {
  if (!c) return 0;
  let n = 0;
  if (c.triage.status === 'complete') n += 1;
  if (c.resources.status === 'complete') n += 1;
  for (const [key, d] of Object.entries(c.dimensionData)) {
    if (key === 'resources') continue; // bereits oben gezählt
    if (d.status === 'complete') n += 1;
  }
  return n;
}

/**
 * Effektive Burn-Rate: explizit gesetzter Wert ODER monatliche Kosten als
 * Default. Liefert null, wenn weder Burn-Rate noch Kosten gesetzt sind.
 */
export function effectiveBurnRateCents(r: ResourcesDraft): number | null {
  if (r.burnRateCents !== null) return r.burnRateCents;
  if (r.costsCents !== null) return r.costsCents;
  return null;
}

/**
 * Selektor: Resources-Daten im Format, das `computeResourcesScore` direkt
 * konsumieren kann (`ResourcesData` aus `@tragfaehigkeit/core`). Die
 * Score-Engine selbst bekommt das `FinancialDataset` separat — hier liefern
 * wir nur die qualitativen ResourcesData-Felder.
 */
export function selectResourcesData(c: WizardCaseState | undefined): ResourcesData | null {
  if (!c) return null;
  return {
    dimension: 'resources',
    // financialDatasetId bleibt für AP6b leer — die Verbindung zur
    // FinancialDataset-Tabelle wird in Phase 2 ergänzt.
  };
}
